import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";
import {MdPayment, MdTableRestaurant, MdStorefront, MdRestaurantMenu, MdEventAvailable} from "react-icons/md";
import {FaBoxOpen, FaTag, FaPercentage, FaTruck, FaCreditCard, FaMoneyBillWave} from "react-icons/fa";
import {BiCategory} from "react-icons/bi";
import {Maximize2, Minimize2} from "lucide-react";
import {fundMovementCategoryLabel} from "../fundMovementCategories";
import FundMovementsByCategory from "./FundMovementsByCategory";

// ── Helper: detect card payment by name ──────────────────────────────────────
const normalize = (str = '') =>
    str
        .toLowerCase()
        .normalize('NFD') // sépare les accents
        .replace(/[\u0300-\u036f]/g, ''); // supprime les accents

const isCardPayment = (name = '') => {
    const n = normalize(name);
    return /\b(carte|cb|cp|credit|debit|visa|mastercard)\b/i.test(n);
};

// ── Commission Modal — pure front-end, zero API calls ─────────────────────────
const CommissionModal = ({item, onClose, isDarkMode}) => {
    const {t} = useTranslation("activite");
    const [rate, setRate] = useState('1.5');
    const total = Number(item.total || 0);
    const rateNum = Math.min(Math.max(parseFloat(rate) || 0, 0), 10);
    const commission = total * rateNum / 100;
    const net = total - commission;
    const invalid = parseFloat(rate) > 10 || parseFloat(rate) < 0;

    const fmt = (v) =>
        v.toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' DH';

    const bg = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
    const inputCls = isDarkMode
        ? 'bg-gray-700 border-gray-600 text-white focus:ring-primary/40 focus:border-primary'
        : 'bg-gray-50 border-gray-200 text-gray-900 focus:ring-primary/20 focus:border-primary';
    const divider = isDarkMode ? 'border-gray-600' : 'border-gray-200';
    const resultBg = isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className={`rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden ${bg}`}
                 onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className={`flex items-center justify-between px-5 py-4 border-b ${divider}`}>
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                            <FaCreditCard size={15} className="text-blue-600 dark:text-blue-400"/>
                        </div>
                        <div>
                            <p className="text-sm font-semibold">{t("expandableTable.commissionModal.title")}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">{item.nom || item.name}</p>
                        </div>
                    </div>
                    <button onClick={onClose}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 dark:text-gray-500 text-lg leading-none">✕
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">{t("expandableTable.commissionModal.rateLabel")}</label>
                        <div className="relative">
                            <input
                                type="number" value={rate} min="0" max="10" step="0.1" autoFocus
                                onChange={e => setRate(e.target.value)}
                                className={`w-full px-3 py-2.5 pr-10 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all ${inputCls}`}
                                placeholder="1.5"
                            />
                            <span
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 dark:text-gray-500 font-medium">%</span>
                        </div>
                        {invalid && <p className="text-xs text-red-500 mt-1">{t("expandableTable.commissionModal.rateInvalid")}</p>}
                    </div>

                    <div className={`rounded-xl border p-4 space-y-2.5 ${resultBg}`}>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t("expandableTable.commissionModal.totalPayment")}</span>
                            <span className="font-semibold">{fmt(total)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t("expandableTable.commissionModal.rate")}</span>
                            <span className="font-semibold text-blue-600 dark:text-blue-400">{rateNum.toFixed(2)} %</span>
                        </div>
                        <div className={`border-t pt-2.5 ${divider}`}/>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t("expandableTable.commissionModal.commission")}</span>
                            <span className="font-semibold text-red-600 dark:text-red-400">− {fmt(commission)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold">{t("expandableTable.commissionModal.netAmount")}</span>
                            <span className="text-base font-bold text-primary">{fmt(net)}</span>
                        </div>
                    </div>
                </div>

                <div className={`px-5 py-3 border-t ${divider}`}>
                    <button onClick={onClose}
                            className="w-full py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-xl transition-colors">
                        {t("expandableTable.commissionModal.close")}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main ExpandableTable ──────────────────────────────────────────────────────
const ExpandableTable = ({
                             isExpanded,
                             toggleExpand,
                             activeTab,
                             setActiveTab,
                             tabOptions,
                             className = "",
                             _dateRange,
                             _startTime,
                             _endTime,
                             isDarkMode = false,
                             refreshKey = 0,
                         }) => {
    const {t} = useTranslation("activite");
    const [data, setData] = useState([]);
    const [orderTypeData, setOrderTypeData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [commissionItem, setCommissionItem] = useState(null);

    const formatCurrency = (value) =>
        Number(value || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' DH';

    // Plein écran : la page derrière ne doit plus défiler.
    useEffect(() => {
        if (!isExpanded) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isExpanded]);

    // Le filtre de période de la page est masqué en plein écran : on le
    // rappelle dans l'en-tête.
    const periodLabel = (() => {
        const range = _dateRange?.[0];
        if (!range?.startDate) return "";
        const start = toLocalDateStr(range.startDate);
        const end = toLocalDateStr(range.endDate || range.startDate);
        const fmt = (d) => d.split("-").reverse().join("/");
        const dates = start === end ? fmt(start) : `${fmt(start)} → ${fmt(end)}`;
        const hasTimeWindow = _startTime && _endTime && !(_startTime === "00:00" && _endTime === "23:59");
        return hasTimeWindow ? `${dates} · ${_startTime}–${_endTime}` : dates;
    })();

    const getTabIcon = (tabKey) => {
        const p = {className: "inline-block mr-2", size: 16};
        switch (tabKey) {
            case "payments":
                return <MdPayment {...p}/>;
            case "taxes":
                return <FaTag {...p}/>;
            case "mouvements":
                return <FaMoneyBillWave {...p}/>;
            case "produits":
                return <FaBoxOpen {...p}/>;
            case "categories":
                return <BiCategory {...p}/>;
            case "remises":
                return <FaPercentage {...p}/>;
            case "menus":
                return <MdRestaurantMenu {...p}/>;
            case "modes":
                return <MdTableRestaurant {...p}/>;
            case "channels":
                return <MdStorefront {...p}/>;
            case "rendezvous":
                return <MdEventAvailable {...p}/>;
            default:
                return <MdPayment {...p}/>;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!_dateRange?.[0]) return;
            // L'onglet Mouvements charge ses propres données
            // (FundMovementsByCategory).
            if (activeTab === "mouvements") {
                setData([]);
                setOrderTypeData(null);
                return;
            }
            setLoading(true);
            try {
                const start = toLocalDateStr(_dateRange[0].startDate);
                const end = toLocalDateStr(_dateRange[0].endDate);
                const startTime = _startTime || "00:00";
                const endTime = _endTime || "23:59";
                const accountId = await secureStorage.getItem("selectedAccount");

                if (activeTab === "modes") {
                    const res = await api.get(
                        `/activite/order_type_metrics?startDate=${start}&endDate=${end}&startTime=${startTime}&endTime=${endTime}`,
                        {headers: {"X-account-id": accountId.id, "Accept": "application/json"}}
                    );
                    setOrderTypeData(res.data || null);
                    setData([]);
                } else if (activeTab === "channels") {
                    const res = await api.get(
                        `/activite/channel_metrics?startDate=${start}&endDate=${end}&startTime=${startTime}&endTime=${endTime}`,
                        {headers: {"X-account-id": accountId.id, "Accept": "application/json"}}
                    );
                    // Même format que les autres tableaux : NOM / QTÉ / TOTAL.
                    const channels = Array.isArray(res.data?.channels) ? res.data.channels : [];
                    setOrderTypeData(null);
                    setData(channels.map((c) => ({
                        name: c.channel_name || t("expandableTable.noChannel"),
                        quantity: c.orders_count || 0,
                        total: c.total_revenue || 0,
                    })));
                } else if (activeTab === "rendezvous") {
                    const res = await api.get(
                        `/activite/appointment_origins?startDate=${start}&endDate=${end}&startTime=${startTime}&endTime=${endTime}`,
                        {headers: {"X-account-id": accountId.id, "Accept": "application/json"}}
                    );
                    setOrderTypeData(null);
                    // Pas de lignes à 0 partout : même état vide que les autres tableaux.
                    setData(res.data?.total > 0 ? res.data.origins : []);
                } else {
                    setOrderTypeData(null);
                    const endpoints = {
                        payments: '/activite/payment_totals',
                        taxes: '/activite/tax_totals',
                        mouvements: '/activite/fund_movements_totals',
                        produits: '/activite/product_totals',
                        categories: '/activite/category_totals',
                        remises: '/activite/discount_totals',
                        menus: '/activite/menu_totals',
                    };
                    const endpoint = endpoints[activeTab] || '/activite/payment_totals';
                    const res = await api.get(
                        `${endpoint}?startDate=${start}&endDate=${end}&startTime=${startTime}&endTime=${endTime}`,
                        {
                            headers: {
                                "X-account-id": accountId.id,
                                "Accept": "application/json",
                                "Content-Type": "application/json"
                            }
                        }
                    );
                    setData(res.data);
                }
            } catch (err) {
                console.error(`Error fetching ${activeTab} data:`, err);
                setData([]);
                setOrderTypeData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // `t` intentionally omitted — re-running this on every language change
        // would refetch from the API just to relabel the rare "no channel
        // name" fallback; that fallback simply won't re-translate until the
        // next real refetch, which is an acceptable trade-off here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, _dateRange, _startTime, _endTime, refreshKey]);

    const renderOrderTypeMetrics = () => {
        if (!orderTypeData) return (
            <div className="flex justify-center items-center py-8">
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t("expandableTable.noData")}</p>
            </div>
        );
        const emporter = orderTypeData.emporter || {};
        const surPlace = orderTypeData.sur_place || {};
        const total = orderTypeData.total_orders || 0;
        const types = [
            {
                key: 'emporter',
                data: emporter,
                icon: <FaTruck size={18} className="text-white"/>,
                bg: 'bg-orange-500',
                lightBg: 'bg-orange-50 dark:bg-orange-900/20',
                border: 'border-orange-200 dark:border-orange-800/60',
                bar: 'bg-orange-400',
                text: 'text-orange-700 dark:text-orange-300',
                pct: 'text-orange-600 dark:text-orange-400'
            },
            {
                key: 'sur_place',
                data: surPlace,
                icon: <MdTableRestaurant size={18} className="text-white"/>,
                bg: 'bg-teal-500',
                lightBg: 'bg-teal-50 dark:bg-teal-900/20',
                border: 'border-teal-200 dark:border-teal-800/60',
                bar: 'bg-teal-400',
                text: 'text-teal-700 dark:text-teal-300',
                pct: 'text-teal-600 dark:text-teal-400'
            },
        ];
        return (
            <div className="p-3 space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 px-1">
                    <span className="font-medium">{t("expandableTable.totalOrders")}</span>
                    <span className="font-bold text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{total}</span>
                </div>
                {types.map(({key, data: d, icon, bg, lightBg, border, bar, text, pct}) => {
                    const pctVal = Number(d.percentage || 0);
                    if (!d.label && d.orders_count === undefined) return (
                        <div key={key} className={`rounded-xl border ${border} ${lightBg} p-3`}>
                            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">{t("expandableTable.noDataForType")}</p>
                        </div>
                    );
                    return (
                        <div key={key} className={`rounded-xl border ${border} ${lightBg} p-3 space-y-2`}>
                            <div className="flex items-center gap-2">
                                <div
                                    className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>{icon}</div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${text} truncate`}>{d.label || key}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t("expandableTable.orderCount", {count: d.orders_count || 0})}</p>
                                </div>
                                <span className={`text-sm font-bold ${pct}`}>{pctVal.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                <div className={`${bar} h-1.5 rounded-full transition-all duration-500`}
                                     style={{width: `${Math.min(pctVal, 100)}%`}}/>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 pt-1">
                                {[
                                    {l: t("expandableTable.revenueTtc"), v: formatCurrency(d.total_revenue)},
                                    {l: t("expandableTable.revenueHt"), v: formatCurrency(d.total_revenue_ht)},
                                    {l: t("expandableTable.averageOrder"), v: formatCurrency(d.average_order_value)},
                                    {
                                        l: t("expandableTable.discounts"),
                                        v: (d.total_discounts || 0) > 0 ? `−${formatCurrency(d.total_discounts)}` : '—',
                                        red: (d.total_discounts || 0) > 0
                                    },
                                ].map(k => (
                                    <div key={k.l} className="bg-white/70 dark:bg-black/20 rounded-lg p-1.5">
                                        <p className="text-xs text-gray-400 dark:text-gray-500">{k.l}</p>
                                        <p className={`text-xs font-semibold truncate ${k.red ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>{k.v}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <div
                className={`shadow-sm transition-all duration-500 border-r ${
                    isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                } ${
                    // Plein écran : par-dessus la sidebar/le header (z-50), sous
                    // les modales (CommissionModal, z-[60]). Échap ferme
                    // (géré par ActivityPage).
                    isExpanded
                        ? "fixed inset-0 z-[55] w-full h-[100dvh] overflow-y-auto px-3 sm:px-6 pb-6"
                        : "relative w-full md:w-1/2"
                } ${className}`}
            >
                <div className="table-container">
                    {/* Tab bar — collée en haut en plein écran pour garder onglets
                        et bouton de sortie visibles en défilant. */}
                    <div className={`flex items-center border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} ${
                        isExpanded ? `sticky top-0 z-10 pt-2 ${isDarkMode ? "bg-gray-900" : "bg-white"}` : ""
                    }`}>
                    <div className="flex flex-wrap flex-1 min-w-0">
                        {tabOptions.map((tab) => (
                            <button
                                key={tab.key}
                                className={`py-2.5 px-3 sm:px-4 text-sm font-medium min-w-max flex items-center gap-1 border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === tab.key
                                        ? "text-primary border-primary bg-primary/5"
                                        : isDarkMode
                                            ? "text-gray-400 border-transparent hover:text-gray-300 hover:border-gray-600"
                                            : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                                }`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {getTabIcon(tab.key)}
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    {isExpanded && periodLabel && (
                        <span className={`hidden sm:inline-flex flex-shrink-0 items-center ml-2 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"
                        }`}>
                            {periodLabel}
                        </span>
                    )}
                    {toggleExpand && (
                        <button
                            type="button"
                            onClick={toggleExpand}
                            title={t(isExpanded ? "expandableTable.collapse" : "expandableTable.expand")}
                            aria-label={t(isExpanded ? "expandableTable.collapse" : "expandableTable.expand")}
                            aria-pressed={isExpanded}
                            className={`flex-shrink-0 flex items-center justify-center h-7 w-7 mx-2 rounded-full transition-colors ${
                                isExpanded
                                    ? "bg-primary/10 text-primary hover:bg-primary/20"
                                    : isDarkMode
                                        ? "text-gray-400 hover:text-gray-100 hover:bg-gray-800"
                                        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                            }`}
                        >
                            {isExpanded
                                ? <Minimize2 size={14} strokeWidth={2.25}/>
                                : <Maximize2 size={14} strokeWidth={2.25}/>}
                        </button>
                    )}
                    </div>

                    {activeTab === "mouvements" ? (
                        <FundMovementsByCategory
                            _dateRange={_dateRange}
                            _startTime={_startTime}
                            _endTime={_endTime}
                            isDarkMode={isDarkMode}
                            refreshKey={refreshKey}
                        />
                    ) : loading ? (
                        <div className="flex justify-center items-center py-8">
                            <div
                                className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full mr-2"/>
                            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t("expandableTable.loading")}</p>
                        </div>
                    ) : activeTab === "modes" ? renderOrderTypeMetrics() : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse mt-4">
                                    <thead>
                                    <tr>
                                        <th className={`text-left py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.name")}</th>
                                        <th className={`text-left py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.quantity")}</th>
                                        {activeTab === "mouvements" && (
                                            <>
                                                <th className={`text-right py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.in")}</th>
                                                <th className={`text-right py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t("expandableTable.columns.out")}</th>
                                            </>
                                        )}
                                        <th className={`text-right py-2 px-4 text-xs font-medium border-b ${isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"}`}>{t(activeTab === "rendezvous" ? "expandableTable.columns.share" : "expandableTable.columns.total")}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {data.map((item, index) => {
                                        const itemName = activeTab === "rendezvous"
                                            ? t(`appointmentOrigins.${item.key}`)
                                            : activeTab === "mouvements"
                                            ? fundMovementCategoryLabel(t, item.category)
                                            : item.nom || item.name || item.category || "";
                                        const showCommission = activeTab === "payments" && isCardPayment(itemName);
                                        return (
                                            <tr key={index}
                                                className={`transition-colors ${isDarkMode ? "hover:bg-gray-800 border-gray-700" : "hover:bg-gray-50"}`}>
                                                <td className={`py-3 px-4 text-sm ${activeTab === "rendezvous" ? "" : "capitalize"} ${isDarkMode ? "text-gray-300 border-gray-700" : "text-gray-700"}`}>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span>{itemName}</span>
                                                        {showCommission && (
                                                            <button
                                                                onClick={() => setCommissionItem(item)}
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/60 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors whitespace-nowrap"
                                                                title={t("expandableTable.calculateCommission")}
                                                            >
                                                                <FaCreditCard size={10}/>
                                                                {t("expandableTable.commission")}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className={`py-3 px-4 text-sm ${isDarkMode ? "text-gray-300 border-gray-700" : "text-gray-700"}`}>{item.qte || item.quantity || item.count || 0}</td>
                                                {activeTab === "mouvements" && (
                                                    <>
                                                        <td className="py-3 px-4 text-sm text-right font-medium text-green-600 dark:text-green-400">
                                                            {item.entreesTotal ? formatCurrency(item.entreesTotal) : '—'}
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-right font-medium text-red-600 dark:text-red-400">
                                                            {item.sortiesTotal ? formatCurrency(item.sortiesTotal) : '—'}
                                                        </td>
                                                    </>
                                                )}
                                                <td className={`py-3 px-4 text-sm text-right font-medium ${isDarkMode ? "text-gray-200 border-gray-700" : "text-gray-700"}`}>
                                                    {activeTab === "rendezvous" ? `${item.percentage}%` : formatCurrency(item.total || 0)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                    {activeTab === "rendezvous" && data.length > 0 && (
                                        <tfoot>
                                        <tr className={`border-t font-semibold ${isDarkMode ? "border-gray-700 text-gray-200" : "border-gray-200 text-gray-800"}`}>
                                            <td className="py-3 px-4 text-sm">{t("appointmentOrigins.total")}</td>
                                            <td className="py-3 px-4 text-sm">{data.reduce((sum, item) => sum + item.count, 0)}</td>
                                            <td className="py-3 px-4 text-sm text-right">100%</td>
                                        </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                            {!loading && data.length === 0 && (
                                <div className="flex justify-center items-center py-8">
                                    <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("expandableTable.noData")}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {commissionItem && (
                <CommissionModal
                    item={commissionItem}
                    onClose={() => setCommissionItem(null)}
                    isDarkMode={isDarkMode}
                />
            )}
        </>
    );
};

export default ExpandableTable;
